import { useCallback, useRef } from 'react'
import { Box, Group, ActionIcon, Tooltip, rem } from '@mantine/core'
import { Select } from '../../Common/Select'
import { TextInput } from '../../Common/TextInput'
import { Button } from '../../Common/Button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch, faXmark } from '@fortawesome/sharp-duotone-solid-svg-icons'
import type { SearchOptions } from '../../../types/search'

// The dropdown and input match the 40px buttons, so the search row lines up.
const FIELD_40_STYLES = { input: { height: rem(40), minHeight: rem(40) } }

const PLACEHOLDER_MAP: Record<string, string> = {
  reference: 'Enter Reference Number',
  insuredName: 'Enter Insured Name',
  policyNumber: 'Enter Policy Number',
  customerReference: 'Enter Customer Reference',
  client: 'Enter Client Name',
  broker: 'Enter Broker Name',
  filename: 'Enter Filename',
}

// Convert MM/DD/YYYY → YYYY-MM-DD for <input type="date"> value prop
function toDateInputValue(mmddyyyy: string): string {
  if (!mmddyyyy) return ''
  const [m, d, y] = mmddyyyy.split('/')
  return y && m && d ? `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}` : ''
}

// Convert YYYY-MM-DD (from native date input) → MM/DD/YYYY for state/API
function fromDateInputValue(yyyymmdd: string): string {
  if (!yyyymmdd) return ''
  const [y, m, d] = yyyymmdd.split('-')
  return `${m}/${d}/${y}`
}

export interface SearchBarProps {
  searchType: string | null
  searchOptions: SearchOptions[]
  searchValue: string
  onSearchValueChange: (value: string) => void
  onSearchTypeChange: (value: string | null) => void
  onSearch: () => void
  onClear: () => void
}

export function SearchBar({
  searchType,
  searchOptions,
  searchValue,
  onSearchTypeChange,
  onSearchValueChange,
  onSearch,
  onClear,
}: Readonly<SearchBarProps>) {
  const isDateOfLoss = searchType === 'dateOfLoss'
  const placeholder = PLACEHOLDER_MAP[searchType || 'reference'] || 'Search...'
  const inputRef = useRef<HTMLInputElement>(null)

  const handleClear = useCallback(() => {
    onClear()
    inputRef.current?.focus()
  }, [onClear])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') onSearch()
      if (event.key === 'Escape') handleClear()
    },
    [onSearch, handleClear]
  )

  const clearButton = searchValue ? (
    <Tooltip label="Clear search" position="left" withArrow>
      <ActionIcon variant="transparent" size="xs" onClick={handleClear} aria-label="Clear search">
        <FontAwesomeIcon icon={faXmark} />
      </ActionIcon>
    </Tooltip>
  ) : null

  const searchInput = (
    <TextInput
      ref={inputRef}
      type={isDateOfLoss ? 'date' : 'text'}
      value={isDateOfLoss ? toDateInputValue(searchValue) : searchValue}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
        onSearchValueChange(isDateOfLoss ? fromDateInputValue(event.currentTarget.value) : event.currentTarget.value)
      }
      onKeyDown={handleKeyDown}
      placeholder={isDateOfLoss ? undefined : placeholder}
      aria-label="Search claims"
      size="xs"
      styles={FIELD_40_STYLES}
      style={{ flex: 1 }}
      rightSection={clearButton}
    />
  )

  return (
    <>
      {/* Narrow layout: single row with an icon button */}
      <Box hiddenFrom="xs">
        <Group wrap="nowrap" gap="xs">
          <Select
            data={searchOptions}
            value={searchType}
            onChange={onSearchTypeChange}
            size="xs"
            w={130}
            styles={FIELD_40_STYLES}
            aria-label="Search type"
          />
          {searchInput}
          <ActionIcon
            variant="filled"
            size={40}
            onClick={onSearch}
            aria-label="Search"
            style={{ backgroundColor: 'var(--mantine-color-brand-filled)', color: 'var(--mantine-color-white)' }}
          >
            <FontAwesomeIcon icon={faSearch} size="sm" />
          </ActionIcon>
        </Group>
      </Box>

      {/* Wide layout: single row with a text button */}
      <Box visibleFrom="xs">
        <Group wrap="nowrap" gap="xs">
          <Select
            data={searchOptions}
            value={searchType}
            onChange={onSearchTypeChange}
            size="xs"
            w={130}
            styles={FIELD_40_STYLES}
            aria-label="Search type"
          />
          {searchInput}
          <Button label="Search" size="xs" leftSection={<FontAwesomeIcon icon={faSearch} />} onClick={onSearch} />
        </Group>
      </Box>
    </>
  )
}
